import type { CSSProperties } from "react";
import { isLegacy, isPermanent, type UserBadge, type BadgeKind } from "@/hooks/useUserBadges";

interface Props {
  badge: UserBadge;
  /** sm reduces the chip ~20% for fotw_candidate. md is default. */
  size?: "sm" | "md";
  onClick?: () => void;
}

// ── Variant display tables ──────────────────────────────────────────────────
// Keep aligned with badge_thresholds.variant values in the SQL migration.
// Drift here = bad labels in the UI, not a data integrity bug.

const COLLECTOR_TIER_NAMES: Record<string, string> = {
  sprout:       "Sprout",
  picker:       "Picker",
  rack_runner:  "Rack Runner",
  archivist:    "Archivist",
  conservator:  "Conservator",
};

const REGION_VARIANT_NAMES: Record<string, string> = {
  hawaii_5o:        "Hawaii 5-O",
  harajuku_hunter:  "Harajuku Hunter",
  happy_in_paris:   "Happy in Paris",
  wild_west:        "Wild West",
  italian_mobster:  "Italian Mobster",
  tea_and_vintage:  "Tea & Vintage",
};

const STYLE_VARIANT_NAMES: Record<string, string> = {
  gossip_girl:         "Gossip Girl",
  sports_connoisseur:  "Sports Connoisseur",
  from_the_streets:    "From the Streets",
};

const STREAK_VARIANT_NAMES: Record<string, string> = {
  bestie:        "Bestie",
  cheerleader:   "Cheerleader",
  photo_pro:     "Photo Pro",
  stack_master:  "Stack Master",
  grail_granter: "Grail Granter",
};

const DIVERSE_VARIANT_NAMES: Record<string, string> = {
  time_traveler: "Time Traveler",
};

const STREAK_VARIANT_SUBLINES: Record<string, string> = {
  bestie:        "14-day streak",
  cheerleader:   "25 notes",
  photo_pro:     "10 PASS streak",
  stack_master:  "5 in 24h",
  grail_granter: "Granted a grail",
};

const REGION_VARIANT_SUBLINES: Record<string, string> = {
  hawaii_5o:        "Hawaiian",
  harajuku_hunter:  "Japanese",
  happy_in_paris:   "French",
  wild_west:        "Western",
  italian_mobster:  "Italian",
  tea_and_vintage:  "British",
};

const STYLE_VARIANT_SUBLINES: Record<string, string> = {
  gossip_girl:         "Preppy",
  sports_connoisseur:  "Sportswear",
  from_the_streets:    "Streetwear",
};

/** Human label for a badge — falls back to the kind name when metadata missing. */
export function badgeLabel(badge: UserBadge): string {
  const m = (badge.metadata ?? {}) as Record<string, string | null | undefined>;
  switch (badge.kind) {
    case "collector_tier":     return COLLECTOR_TIER_NAMES[String(m.level ?? "")] ?? "Collector";
    case "era_milestone":      return `${String(m.era ?? "")} Specialist`.trim() || "Era Specialist";
    case "brand_authority":    return String(m.brand ?? "Brand");
    case "diverse_collector":  return DIVERSE_VARIANT_NAMES[String(m.variant ?? "")] ?? "Time Traveler";
    case "region_collector":   return REGION_VARIANT_NAMES[String(m.variant ?? "")] ?? "Region";
    case "style_collector":    return STYLE_VARIANT_NAMES[String(m.variant ?? "")] ?? "Style";
    case "deadstock_collector":return "Deadstock";
    case "community_streak":   return STREAK_VARIANT_NAMES[String(m.variant ?? "")] ?? "Streak";
    case "era_specialist":     return String(m.era ?? "Era");
    case "fotw_winner":        return "Find of the Week";
    case "fotw_candidate":     return "FotW Candidate";
    case "founder":            return "Founder";
    case "verified_moderator": return "Verified Mod";
    default:                   return String((badge as { kind: string }).kind);
  }
}

// ── Compact labels for the sm chip strip ────────────────────────────────────
// When a chip is rendered in the cramped profile strip, anything longer than
// ~9 chars eats into its neighbor. Rule: drop to the first meaningful word
// when it's still recognizable, otherwise fall back to capital initials.
// Single-word labels under the threshold are kept verbatim.

const SHORT_LABEL_THRESHOLD = 9;

const SHORT_LABEL_OVERRIDES: Record<string, string> = {
  "Find of the Week":   "FotW",
  "FotW Candidate":     "FotW",
  "Verified Mod":       "Verified",
  "Time Traveler":      "Time",
  "Stack Master":       "Stack",
  "Rack Runner":        "Rack",
  "Hawaii 5-O":         "Hawaii",
  "Harajuku Hunter":    "Harajuku",
  "Happy in Paris":     "Paris",
  "Italian Mobster":    "Italian",
  "Tea & Vintage":      "British",
  "Wild West":          "Western",
  "Gossip Girl":        "Preppy",
  "Sports Connoisseur": "Sports",
  "From the Streets":   "Streets",
  "Photo Pro":          "Photo",
  "Grail Granter":      "Grail",
  "Cheerleader":        "Cheer",
};

function compactLabel(full: string): string {
  if (full.length <= SHORT_LABEL_THRESHOLD) return full;
  const override = SHORT_LABEL_OVERRIDES[full];
  if (override) return override;
  const words = full.split(/\s+/).filter(Boolean);
  if (words.length === 1) return full; // single long word — let chip scroll
  // Use first word if it's still short enough, else fall back to initials.
  if (words[0].length <= SHORT_LABEL_THRESHOLD) return words[0];
  return words.map((w) => w[0]?.toUpperCase() ?? "").join("");
}

/** Tiny subline under the main label (e.g. "Authority" under "Carhartt"). */
export function badgeSubline(badge: UserBadge): string | null {
  const m = (badge.metadata ?? {}) as Record<string, string | null | undefined>;
  switch (badge.kind) {
    case "collector_tier":     return "Collector tier";
    case "era_milestone":      return "Era milestone";
    case "brand_authority":    return "Authority";
    case "diverse_collector":  return "4+ eras";
    case "region_collector":   return REGION_VARIANT_SUBLINES[String(m.variant ?? "")] ?? null;
    case "style_collector":    return STYLE_VARIANT_SUBLINES[String(m.variant ?? "")] ?? null;
    case "deadstock_collector":return "Deadstock";
    case "community_streak":   return STREAK_VARIANT_SUBLINES[String(m.variant ?? "")] ?? null;
    case "era_specialist":     return "Specialist (legacy)";
    case "fotw_winner":        return formatWeekStart(badge.metadata?.week_start as string);
    case "fotw_candidate":     return formatWeekStart(badge.metadata?.week_start as string);
    case "verified_moderator": return "Trusted reviewer";
    default:                   return null;
  }
}

function formatWeekStart(iso?: string | null): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return null;
  }
}

/**
 * BadgeChip — visual rules:
 *   - Permanent kinds: solid 1.5px ring
 *   - Rolling (fotw_candidate): dashed 1.5px ring + opacity 0.7 + sm size
 *   - Legacy (era_specialist): solid ring + opacity 0.55 + muted color
 *   - expires_at set: opacity 0.4, non-interactive
 */
export function BadgeChip({ badge, size = "md", onClick }: Props) {
  const expired = !!badge.expires_at;
  const isCandidate = badge.kind === "fotw_candidate";
  const legacy = isLegacy(badge.kind);
  const effectiveSize = isCandidate ? "sm" : size;
  const permanent = isPermanent(badge.kind);

  const interactive = !expired && !!onClick;

  const baseStyle: CSSProperties = {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    minWidth: effectiveSize === "sm" ? 64 : 80,
    padding: effectiveSize === "sm" ? "6px 10px" : "8px 12px",
    background: "transparent",
    color: legacy ? "hsl(var(--ink-2))" : "hsl(var(--ink))",
    border: `1.5px ${permanent ? "solid" : "dashed"} hsl(${legacy ? "var(--ink-2)" : "var(--ink)"})`,
    borderRadius: 14,
    cursor: interactive ? "pointer" : "default",
    opacity: expired ? 0.4 : isCandidate ? 0.7 : legacy ? 0.55 : 1,
    fontFamily: "'IBM Plex Mono', monospace",
    textAlign: "center",
    lineHeight: 1.05,
  };

  const fullLabel = badgeLabel(badge);
  // sm chips (profile badge strip): compact long labels to one word or
  // initials so they don't overlap in the cramped row. Full label is
  // preserved in the aria-label so screen readers still get the real name.
  const label = effectiveSize === "sm" ? compactLabel(fullLabel) : fullLabel;
  // Hide sublines on sm chips for the same reason. md chips (Badges page,
  // detail modal) keep both the full label and the subline.
  const subline = effectiveSize === "sm" ? null : badgeSubline(badge);

  return (
    <button
      type="button"
      onClick={interactive ? onClick : undefined}
      disabled={!interactive}
      aria-label={`Badge: ${fullLabel}${subline ? ` · ${subline}` : ""}`}
      style={baseStyle}
    >
      <span style={{
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontWeight: 700,
        fontSize: effectiveSize === "sm" ? 11 : 13,
        color: legacy ? "hsl(var(--ink-2))" : "hsl(var(--ink))",
        whiteSpace: "nowrap",
      }}>
        {label}
      </span>
      {subline && (
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: effectiveSize === "sm" ? 8 : 9,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "hsl(var(--ink-2))",
          marginTop: 1,
        }}>
          {subline}
        </span>
      )}
    </button>
  );
}

/** Internal — exported so the Badges page can use the same logic. */
export function _badgeKindLabel(kind: BadgeKind): string {
  switch (kind) {
    case "collector_tier":     return "Collector Tier";
    case "era_milestone":      return "Era Milestone";
    case "brand_authority":    return "Brand Authority";
    case "diverse_collector":  return "Diverse Collector";
    case "region_collector":   return "Region Collector";
    case "style_collector":    return "Style Collector";
    case "deadstock_collector":return "Deadstock Collector";
    case "community_streak":   return "Community Streak";
    case "era_specialist":     return "Era Specialist (legacy)";
    case "fotw_winner":        return "Find of the Week";
    case "fotw_candidate":     return "Find of the Week Candidate";
    case "founder":            return "Founder";
    case "verified_moderator": return "Verified Moderator";
    default:                   return String(kind);
  }
}
